import React, { FC, useEffect, useState } from 'react';
import Modal from '@components/Modal';
import { mixpanelTrack } from '@providers/Mixpanel';
import {
  StripeCardCvcElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardNumberElementChangeEvent,
  StripeError,
} from '@stripe/stripe-js';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { CARD_ELEMENT_OPTIONS } from '@helpers/checkout';
import Buttons from '@components/Buttons';
import Spacer from '@components/Spacer';
import * as Types from '@typings/graphql-models';
import * as tsClient from '@tsClient';
import useUserProfile from '@providers/Auth/useUserProfile';
import useNoodleApi from '@hooks/useNoodleApi';
import { Checkbox } from '@/components/DesignLibrary/Atoms';
import BannerStatus from '@/components/DesignLibrary/Atoms/BannerStatus';
import s from './PaymentMethodEditModal.module.scss';

type Props = {
  creator: Pick<Types.Creator, 'id' | 'stripe_account_id'>;
  onClose: () => void;
  onSaveCard?: () => Promise<void>;
  personId: string;
  existingCard?: {
    brand: string;
    exp_month: number;
    exp_year: number;
    funding?: string;
    last4: string;
  } | null;
  isDefaultPaymentMethod?: boolean;
  paymentMethodId?: string;
  defaultPaymentMethodId?: string;
  showSetAsDefaultOption?: boolean;
};

type Event = StripeCardNumberElementChangeEvent | StripeCardCvcElementChangeEvent | StripeCardExpiryElementChangeEvent;

const PaymentMethodEditModal: FC<Props> = (
  { creator,
    onClose,
    onSaveCard,
    personId,
    existingCard,
    isDefaultPaymentMethod,
    paymentMethodId,
    defaultPaymentMethodId,
    showSetAsDefaultOption,
  }) => {
  const [error, setError] = useState<StripeError | string | null>(null);
  const [isDisabled, setDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isDefaultPaymentMethodEdit, setIsDefaultPaymentMethod] = useState(isDefaultPaymentMethod);
  const [isUpdatingDefault, setIsUpdatingDefault] = useState(false);
  const [defaultPaymentMethod, _setDefaultPaymentMethodId] = useState<string | null>(defaultPaymentMethodId ?? null);
  const [hasCardNumber, setHasCardNumber] = useState(false);

  const elements = useElements();
  const stripe = useStripe();
  const profile = useUserProfile();
  const { getData: attachNewCardFn } = useNoodleApi(tsClient.attachNewCard);
  const { getData: setAsDefaultPaymentMethodFn } = useNoodleApi(tsClient.paymentMethods.setAsDefaultPaymentMethod);
  const { getData: updatePaymentMethodForPersonFn } = useNoodleApi(tsClient.paymentPlans.updatePaymentMethodForPerson);

  const handleCardEvent = async (event: Event): Promise<void> => {
    if (event.error) {
      setError(event.error.message);
      setDisabled(true);
    } else {
      setError('');
      setDisabled(event.empty);
    }

    // Track if card number has content to disable checkbox
    if (event.elementType === 'cardNumber') {
      setHasCardNumber(!event.empty);
    }
  };

  useEffect(() => {
    mixpanelTrack('Edit payment method');
  }, []);

  const handleDefaultPaymentMethodChange = async (newIsChecked: boolean): Promise<void> => {
    try {
      setIsUpdatingDefault(true);

      if (newIsChecked && paymentMethodId) {
        // Set this payment method as default
        await setAsDefaultPaymentMethodFn({
          creatorId: creator.id,
          paymentMethodId,
        });

        setIsDefaultPaymentMethod(newIsChecked);

        // Refresh the payment methods list
        await onSaveCard?.();
        await updatePaymentMethodForPersonFn({
          currentPaymentMethodId: defaultPaymentMethod,
          paymentMethodId,
        });

      } else if (!newIsChecked) {
        // Prevent unchecking if this is currently the default
        // User should set another card as default instead
        setError('Please set another payment method as default first');
        return;
      }

    } catch (err) {
      setError('Failed to update default payment method');
    } finally {
      setIsUpdatingDefault(false);
    }
  };

  const handleSaveCard = async (): Promise<void> => {
    try {
      setIsLoading(true);
      if (!stripe || !elements) {
        throw new Error(`Stripe not initialized: stripe=${Boolean(stripe)} elements=${Boolean(elements)}`);
      }

      const card = elements?.getElement(CardNumberElement);

      if (!card) {
        throw new Error('Failed to get card from stripe');
      }
      const data = await stripe.createPaymentMethod({
        billing_details: {
          name: profile?.name || 'User',
        },
        card,
        type: 'card',
      });

      const { error: stripeError, paymentMethod } = data;

      if (stripeError) {
        mixpanelTrack('Error in attach card form', { errorReason: stripeError.message });
        setError(stripeError.message as string);
        return;
      }

      if (paymentMethod?.id) {
        await attachNewCardFn({
          creatorId: creator.id,
          isDefaultPaymentMethod: isDefaultPaymentMethod ?? false,
          paymentMethodId: paymentMethod?.id,
          personId,
        });
        if (isDefaultPaymentMethodEdit) {
          await updatePaymentMethodForPersonFn({
            currentPaymentMethodId: defaultPaymentMethod,
            paymentMethodId: paymentMethod?.id,
          });
        }
        await onSaveCard?.();
        onClose();
      }

      setIsLoading(false);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <Modal
      title={'Edit payment method'}
      onClose={onClose}
    >
      <div className={s.container}>
        {existingCard && (
          <>
            <div className={s.currentCard}>
              <p className='caption'>Current payment method</p>
              <Spacer size='8px' />
              <div className={s.cardDisplay}>
                <span>•••• •••• •••• {existingCard.last4}</span>
                <span>{existingCard.exp_month.toString().padStart(2, '0')}/{existingCard.exp_year.toString().slice(-2)}</span>
                <span className={s.brand}>{existingCard.brand.toUpperCase()}</span>
              </div>
            </div>
            <Spacer size='16px' />
          </>
        )}

        <p className='caption'>Card number</p>
        <Spacer size='8px' />
        <div className={s.input}>
          <CardNumberElement options={CARD_ELEMENT_OPTIONS} onChange={handleCardEvent} />
        </div>
        <Spacer />
        <div className={s.double}>
          <div style={{ width: '100%' }}>
            <p className='caption'>Expiration date</p>
            <Spacer size='8px' />
            <div className={s.input}>
              <CardExpiryElement options={CARD_ELEMENT_OPTIONS} onChange={handleCardEvent} />
            </div>
          </div>
          <div style={{ width: '100%' }}>
            <p className='caption'>CVC</p>
            <Spacer size='8px' />
            <div className={s.input}>
              <CardCvcElement options={CARD_ELEMENT_OPTIONS} onChange={handleCardEvent} />
            </div>
          </div>
        </div>
        <Spacer size='4px' />
        {error && <p className={s.error}>{error}</p>}
        <Spacer size='4px' />
        {showSetAsDefaultOption && (!hasCardNumber || isDefaultPaymentMethodEdit) && (
          <>
            <Checkbox
              label={isUpdatingDefault ? 'Updating...' : 'Make default payment method'}
              isChecked={isDefaultPaymentMethodEdit ?? false}
              onChange={handleDefaultPaymentMethodChange}
              disabled={isUpdatingDefault}
            />
            <BannerStatus
              status='warning'
              label={`NOTE: Every active payment plan would be updated to use the new default payment method`}
            />
          </>
        )}
        <Spacer size='10px' />
        <Buttons
          isSecondary
          isFullWidth
          disabled={isDisabled || Boolean(error)}
          onClick={handleSaveCard}
          isFetching={isLoading}
        >
          Save
        </Buttons>
      </div>
    </Modal>
  );
};

export default PaymentMethodEditModal;
